import { useCallback, useEffect, useMemo, useState } from 'react';
import { FIELDS_TO_MASSIVE_ASSIGN } from 'reducers/reducer';

const useMassAssignerTools = ({
  processedProperties,
  fieldsToMassiveAssign,
  dispatch,
  isSortByCrop,
  isSearching = false
}) => {
  const [allPropertiesSelected, setAllPropertiesSelected] = useState(false);

  // Pre-compute a Map<fieldId, Set<cropzoneId>> for O(1) selection lookups
  const selectedFieldMap = useMemo(() => {
    const map = new Map();
    fieldsToMassiveAssign.forEach(f => {
      map.set(f.id, new Set(f.cropzones.map(cz => cz.id)));
    });
    return map;
  }, [fieldsToMassiveAssign]);

  // Every selected crop zone id, for O(1) "is this crop zone selected" checks.
  const selectedCropZoneIds = useMemo(() => {
    const ids = new Set();
    fieldsToMassiveAssign.forEach(f => {
      f.cropzones.forEach(cz => ids.add(cz.id));
    });
    return ids;
  }, [fieldsToMassiveAssign]);

  // Returns a new list; the stored selection is never changed in place.
  const withMissingCropZones = (cropzones, cropZonesToAdd) => {
    const existingCZIds = new Set(cropzones.map(cz => cz.id));
    return [
      ...cropzones,
      ...cropZonesToAdd.filter(cz => !existingCZIds.has(cz.id))
    ];
  };

  // Helper Functions
  const createFieldEntry = (field, farm, cropZones) => ({
    ...field,
    farmName: farm.name,
    farmId: farm.id,
    cropZones
  });

  const getAllFieldsGroupedByFarm = properties => {
    const allFields = [];
    properties.forEach(farm => {
      farm.fields.forEach(field => {
        allFields.push(createFieldEntry(field, farm, [...field.cropzones]));
      });
    });
    return allFields;
  };

  // Adds one field's crop zones for `crop` to the select-all list, merging
  // into the field's existing entry when another crop already added it.
  const addFieldForCrop = (allFields, indexById, field, farm, crop) => {
    const cropZonesToAdd = field.cropzones.filter(cz => cz.crop.id === crop.id);
    if (cropZonesToAdd.length === 0) return;
    const index = indexById.get(field.id);
    if (index === undefined) {
      indexById.set(field.id, allFields.length);
      allFields.push({
        ...createFieldEntry(field, farm, cropZonesToAdd),
        cropzones: [...field.cropzones]
      });
      return;
    }
    // eslint-disable-next-line no-param-reassign
    allFields[index] = {
      ...allFields[index],
      cropzones: withMissingCropZones(
        allFields[index].cropzones,
        cropZonesToAdd
      )
    };
  };

  // One pass, indexing fields by id: a field appears under several crops and
  // collects the crop zones of each.
  const getAllFieldsGroupedByCrop = properties => {
    const allFields = [];
    const indexById = new Map();
    properties.forEach(crop =>
      crop.farms.forEach(farm =>
        farm.fields.forEach(field =>
          addFieldForCrop(allFields, indexById, field, farm, crop)
        )
      )
    );
    return allFields;
  };

  // Selection Check Functions using O(1) Map lookups
  const isFieldFullySelected = useCallback(
    field => {
      const czSet = selectedFieldMap.get(field.id);
      if (!czSet) return false;
      return field.cropzones.every(cz => czSet.has(cz.id));
    },
    [selectedFieldMap]
  );

  const isFieldSelectedInCropGroup = useCallback(
    (crop, field) => {
      const cropZonesToCheck = field.cropzones.filter(
        cz => cz.crop.id === crop.id
      );
      if (cropZonesToCheck.length === 0) return false;
      const czSet = selectedFieldMap.get(field.id);
      if (!czSet) return false;
      return cropZonesToCheck.every(cz => czSet.has(cz.id));
    },
    [selectedFieldMap]
  );

  const isFarmSelectedInCropGroup = useCallback(
    (crop, farm) =>
      farm.fields.every(field => isFieldSelectedInCropGroup(crop, field)),
    [isFieldSelectedInCropGroup]
  );

  const isCropGroupFullySelected = useCallback(
    crop => crop.farms.every(farm => isFarmSelectedInCropGroup(crop, farm)),
    [isFarmSelectedInCropGroup]
  );

  const isFarmFullySelected = useCallback(
    farm => farm.fields.every(field => isFieldFullySelected(field)),
    [isFieldFullySelected]
  );

  // While searching, Select All only adds the results to the selection, and
  // clearing it only removes them: selections outside the results are kept.
  const mergeResultsIntoSelection = (results, select) => {
    const byId = new Map(fieldsToMassiveAssign.map(f => [f.id, f]));
    results.forEach(result => {
      const existing = byId.get(result.id);
      if (select) {
        byId.set(
          result.id,
          existing
            ? {
                ...existing,
                cropzones: withMissingCropZones(
                  existing.cropzones,
                  result.cropzones
                )
              }
            : result
        );
        return;
      }
      if (!existing) return;
      const removed = new Set(result.cropzones.map(cz => cz.id));
      const remaining = existing.cropzones.filter(cz => !removed.has(cz.id));
      if (remaining.length) {
        byId.set(result.id, { ...existing, cropzones: remaining });
      } else {
        byId.delete(result.id);
      }
    });
    return Array.from(byId.values());
  };

  const handleSelectAllProperties = useCallback(() => {
    const newSelectedValue = !allPropertiesSelected;
    setAllPropertiesSelected(newSelectedValue);
    const allFields = isSortByCrop
      ? getAllFieldsGroupedByCrop(processedProperties)
      : getAllFieldsGroupedByFarm(processedProperties);
    let payload = newSelectedValue ? allFields : [];
    if (isSearching) {
      payload = mergeResultsIntoSelection(allFields, newSelectedValue);
    }
    dispatch({
      type: FIELDS_TO_MASSIVE_ASSIGN,
      payload
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allPropertiesSelected,
    isSortByCrop,
    isSearching,
    processedProperties,
    fieldsToMassiveAssign,
    dispatch
  ]);

  useEffect(() => {
    if (!fieldsToMassiveAssign.length) {
      setAllPropertiesSelected(false);
    }
  }, [fieldsToMassiveAssign]);

  // useEffect Hook for Updating Selected All Properties State
  useEffect(() => {
    let allSelected = false;
    if (processedProperties && processedProperties.length > 0) {
      if (isSortByCrop) {
        allSelected = processedProperties.every(
          crop => crop.farms && isCropGroupFullySelected(crop)
        );
      } else {
        allSelected = processedProperties.every(
          farm => farm.fields && isFarmFullySelected(farm)
        );
      }
    }
    setAllPropertiesSelected(allSelected);
  }, [
    processedProperties,
    isSortByCrop,
    isCropGroupFullySelected,
    isFarmFullySelected
  ]);

  // Updates `updatedFields` (a copy of the selection) for one field of a crop
  // group, replacing entries rather than changing them in place.
  const updateFieldInCropGroup = (
    updatedFields,
    field,
    farm,
    crop,
    allSelected
  ) => {
    const cropzonesToHandle = field.cropzones.filter(
      cz => cz.crop.id === crop.id
    );
    const fieldIndex = updatedFields.findIndex(f => f.id === field.id);
    if (fieldIndex >= 0) {
      const existingField = updatedFields[fieldIndex];
      if (allSelected) {
        const remaining = existingField.cropzones.filter(
          cz => cz.crop.id !== crop.id
        );
        if (remaining.length === 0) {
          updatedFields.splice(fieldIndex, 1);
        } else {
          // eslint-disable-next-line no-param-reassign
          updatedFields[fieldIndex] = {
            ...existingField,
            cropzones: remaining
          };
        }
      } else {
        // eslint-disable-next-line no-param-reassign
        updatedFields[fieldIndex] = {
          ...existingField,
          cropzones: withMissingCropZones(
            existingField.cropzones,
            cropzonesToHandle
          )
        };
      }
    } else if (!allSelected) {
      updatedFields.push({
        ...field,
        farmName: farm ? farm.name : field.farmName,
        farmId: farm ? farm.id : field.farmId,
        cropzones: cropzonesToHandle
      });
    }
  };

  // Functions for grouping by farms
  const handleSelectedFarm = useCallback(
    farm => {
      if (isSortByCrop) return;
      let updatedFields = [...fieldsToMassiveAssign];
      const allFieldsSelected = farm.fields.every(field =>
        isFieldFullySelected(field)
      );
      if (allFieldsSelected) {
        const farmFieldIds = new Set(farm.fields.map(field => field.id));
        updatedFields = updatedFields.filter(f => !farmFieldIds.has(f.id));
      } else {
        const indexById = new Map(updatedFields.map((f, i) => [f.id, i]));
        farm.fields.forEach(field => {
          const fieldIndex = indexById.get(field.id);
          if (fieldIndex !== undefined) {
            updatedFields[fieldIndex] = {
              ...updatedFields[fieldIndex],
              cropzones: [...field.cropzones]
            };
          } else {
            updatedFields.push({
              ...field,
              farmName: farm.name,
              farmId: farm.id,
              cropzones: [...field.cropzones]
            });
          }
        });
      }
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: updatedFields
      });
    },
    [isSortByCrop, fieldsToMassiveAssign, isFieldFullySelected, dispatch]
  );

  const isFieldSelected = useCallback(
    field => {
      if (isSortByCrop) return false;
      return isFieldFullySelected(field);
    },
    [isSortByCrop, isFieldFullySelected]
  );

  const isFarmSelected = useCallback(
    farm => {
      if (isSortByCrop) return false;
      return farm.fields.every(field => isFieldSelected(field));
    },
    [isSortByCrop, isFieldSelected]
  );

  const handleSelectedField = useCallback(
    (farm, field) => {
      if (isSortByCrop) return;
      const updatedFields = [...fieldsToMassiveAssign];
      const fieldIndex = updatedFields.findIndex(f => f.id === field.id);
      const fieldInAssign = updatedFields[fieldIndex];
      const selectedIds = fieldInAssign
        ? new Set(fieldInAssign.cropzones.map(fcz => fcz.id))
        : null;
      const allCropZonesSelected = selectedIds
        ? field.cropzones.every(cz => selectedIds.has(cz.id))
        : false;
      if (fieldInAssign) {
        if (allCropZonesSelected) {
          updatedFields.splice(fieldIndex, 1);
        } else {
          updatedFields[fieldIndex] = {
            ...fieldInAssign,
            cropzones: [...field.cropzones]
          };
        }
      } else {
        updatedFields.push({
          ...field,
          farmName: farm.name,
          farmId: farm.id,
          cropzones: [...field.cropzones]
        });
      }
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: updatedFields
      });
    },
    [isSortByCrop, fieldsToMassiveAssign, dispatch]
  );

  // Functions for grouping by crops
  const isCropGroupSelected = useCallback(
    crop => {
      if (!isSortByCrop) return false;
      return isCropGroupFullySelected(crop);
    },
    [isSortByCrop, isCropGroupFullySelected]
  );

  const isCropZoneSelected = useCallback(
    cropzone => selectedCropZoneIds.has(cropzone.id),
    [selectedCropZoneIds]
  );

  const isPropertyIndeterminate = useCallback(
    property => {
      if (isSortByCrop) return false;
      if (property.fields) {
        const someSelected = property.fields.some(
          field =>
            isFieldSelected(field) ||
            field.cropzones.some(cz => isCropZoneSelected(cz))
        );
        const allSelected = isFarmSelected(property);
        return someSelected && !allSelected;
      }
      if (property.cropzones) {
        const someSelected = property.cropzones.some(cz =>
          isCropZoneSelected(cz)
        );
        const allSelected = property.cropzones.every(cz =>
          isCropZoneSelected(cz)
        );
        return someSelected && !allSelected;
      }
      return false;
    },
    [isSortByCrop, isFieldSelected, isCropZoneSelected, isFarmSelected]
  );

  const isPropertyIndeterminateInCropGroup = useCallback(
    (crop, property) => {
      if (!isSortByCrop) return false;
      if (property.fields) {
        const someSelected = property.fields.some(
          field =>
            isFieldSelectedInCropGroup(crop, field) ||
            field.cropzones.some(cz => isCropZoneSelected(cz))
        );
        const allSelected = isFarmSelectedInCropGroup(crop, property);
        return someSelected && !allSelected;
      }
      if (property.cropzones) {
        const cropZonesToCheck = property.cropzones.filter(
          cz => cz.crop.id === crop.id
        );
        const someSelected = cropZonesToCheck.some(cz =>
          isCropZoneSelected(cz)
        );
        const allSelected =
          cropZonesToCheck.length > 0 &&
          cropZonesToCheck.every(cz => isCropZoneSelected(cz));
        return someSelected && !allSelected;
      }
      return false;
    },
    [
      isSortByCrop,
      isFieldSelectedInCropGroup,
      isCropZoneSelected,
      isFarmSelectedInCropGroup
    ]
  );

  const allPropertiesIndeterminate = useCallback(() => {
    if (isSortByCrop) {
      const someSelected = processedProperties.some(cropGroup => {
        const crop = { id: cropGroup.id, name: cropGroup.name };
        return cropGroup.farms.some(
          farm =>
            (farm?.fields?.length && isFarmSelectedInCropGroup(crop, farm)) ||
            isPropertyIndeterminateInCropGroup(crop, farm)
        );
      });
      const allSelected = processedProperties.every(cropGroup => {
        const crop = { id: cropGroup.id, name: cropGroup.name };
        return cropGroup.farms.every(
          farm =>
            (farm?.fields?.length && isFarmSelectedInCropGroup(crop, farm)) ||
            !farm?.fields?.length
        );
      });
      return someSelected && !allSelected;
    }

    if (!processedProperties || processedProperties.length === 0) return false;
    const someSelected = processedProperties.some(property => {
      return (
        (property.fields?.length && isFarmSelected(property)) ||
        isPropertyIndeterminate(property)
      );
    });
    const allSelected = processedProperties.every(property =>
      isFarmSelected(property)
    );
    return someSelected && !allSelected;
  }, [
    isSortByCrop,
    processedProperties,
    isFarmSelectedInCropGroup,
    isPropertyIndeterminateInCropGroup,
    isFarmSelected,
    isPropertyIndeterminate
  ]);

  const handleSelectedCropGroup = useCallback(
    crop => {
      if (!isSortByCrop) return;
      const allSelected = isCropGroupFullySelected(crop);
      const updatedFields = [...fieldsToMassiveAssign];
      crop.farms.forEach(farm => {
        farm.fields.forEach(field => {
          updateFieldInCropGroup(updatedFields, field, farm, crop, allSelected);
        });
      });
      dispatch({ type: FIELDS_TO_MASSIVE_ASSIGN, payload: updatedFields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSortByCrop, isCropGroupFullySelected, fieldsToMassiveAssign, dispatch]
  );

  const handleSelectedFarmInCropGroup = useCallback(
    (crop, farm) => {
      if (!isSortByCrop) return;
      const allSelected = isFarmSelectedInCropGroup(crop, farm);
      const updatedFields = [...fieldsToMassiveAssign];
      farm.fields.forEach(field => {
        updateFieldInCropGroup(updatedFields, field, farm, crop, allSelected);
      });
      dispatch({ type: FIELDS_TO_MASSIVE_ASSIGN, payload: updatedFields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSortByCrop, isFarmSelectedInCropGroup, fieldsToMassiveAssign, dispatch]
  );

  const handleSelectedFieldInCropGroup = useCallback(
    (crop, field) => {
      if (!isSortByCrop) return;
      const allSelected = isFieldSelectedInCropGroup(crop, field);
      const updatedFields = [...fieldsToMassiveAssign];
      updateFieldInCropGroup(updatedFields, field, null, crop, allSelected);
      dispatch({ type: FIELDS_TO_MASSIVE_ASSIGN, payload: updatedFields });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSortByCrop, isFieldSelectedInCropGroup, fieldsToMassiveAssign, dispatch]
  );

  const handleSelectedCropZone = useCallback(
    (farm, field, cropZone) => {
      const updatedFields = [...fieldsToMassiveAssign];
      const fieldIndex = updatedFields.findIndex(fd => fd.id === field.id);
      if (fieldIndex >= 0) {
        const fieldInAssign = updatedFields[fieldIndex];
        const isAlreadySelected = fieldInAssign.cropzones.some(
          cz => cz.id === cropZone.id
        );
        if (isAlreadySelected) {
          const remaining = fieldInAssign.cropzones.filter(
            cz => cz.id !== cropZone.id
          );
          if (remaining.length === 0) {
            updatedFields.splice(fieldIndex, 1);
          } else {
            updatedFields[fieldIndex] = {
              ...fieldInAssign,
              cropzones: remaining
            };
          }
        } else {
          updatedFields[fieldIndex] = {
            ...fieldInAssign,
            cropzones: [...fieldInAssign.cropzones, cropZone]
          };
        }
      } else {
        updatedFields.push({
          ...field,
          farmName: farm.name,
          farmId: farm.id,
          cropzones: [cropZone]
        });
      }
      dispatch({
        type: FIELDS_TO_MASSIVE_ASSIGN,
        payload: updatedFields
      });
    },
    [fieldsToMassiveAssign, dispatch]
  );

  const isCropGroupIndeterminate = useCallback(
    crop => {
      if (!crop.farms || crop.farms.length === 0) return false;
      const allSelected = crop.farms.every(farm =>
        isFarmSelectedInCropGroup(crop, farm)
      );
      const someSelected = crop.farms.some(
        farm =>
          isFarmSelectedInCropGroup(crop, farm) ||
          isPropertyIndeterminateInCropGroup(crop, farm)
      );
      return someSelected && !allSelected;
    },
    [isFarmSelectedInCropGroup, isPropertyIndeterminateInCropGroup]
  );

  return {
    handleSelectAllProperties,
    isFieldSelectedInCropGroup,
    isFarmSelectedInCropGroup,
    isCropGroupFullySelected,
    isFarmSelected,
    isFieldSelected,
    isCropZoneSelected,
    handleSelectedFarm,
    handleSelectedField,
    isCropGroupSelected,
    handleSelectedCropGroup,
    handleSelectedFarmInCropGroup,
    handleSelectedFieldInCropGroup,
    handleSelectedCropZone,
    allPropertiesSelected,
    isPropertyIndeterminate,
    isPropertyIndeterminateInCropGroup,
    allPropertiesIndeterminate,
    isCropGroupIndeterminate
  };
};

export default useMassAssignerTools;
